import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowLeftRight, Navigation, Timer } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import {
  transportDriversApi,
  tripsApi,
  vehiclesApi,
} from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type {
  TransportAttendanceStatus,
  TripAttendanceMark,
  TripDirection,
  TripRider,
} from '@/types/transport';
import { formatDate, formatDateTime, orDash } from '@/utils/format';
import {
  AttendanceStatusBadge,
  Badge,
  Field,
  Stat,
  TripStatusBadge,
  delayText,
  formatTime,
  humanise,
} from './shared';

/**
 * The marks a tap can make, per direction. The vocabulary is presentation:
 * whether a mark may be recorded at all — cancelled trip, closed manifest,
 * guest riders switched off — is the server's ruling, surfaced verbatim.
 */
const MARKS: Record<
  TripDirection,
  { value: TransportAttendanceStatus; label: string; tone: string }[]
> = {
  PICKUP: [
    { value: 'BOARDED', label: 'Boarded', tone: 'bg-success/10 text-success border-success/30' },
    { value: 'NOT_BOARDED', label: 'No show', tone: 'bg-danger/10 text-danger border-danger/30' },
    { value: 'ABSENT', label: 'Absent', tone: 'bg-warning/10 text-warning border-warning/30' },
    { value: 'EXCUSED', label: 'Excused', tone: 'bg-info/10 text-info border-info/30' },
  ],
  DROPOFF: [
    {
      value: 'DROPPED_OFF',
      label: 'Dropped off',
      tone: 'bg-success/10 text-success border-success/30',
    },
    { value: 'NOT_BOARDED', label: 'No show', tone: 'bg-danger/10 text-danger border-danger/30' },
    { value: 'ABSENT', label: 'Absent', tone: 'bg-warning/10 text-warning border-warning/30' },
    { value: 'EXCUSED', label: 'Excused', tone: 'bg-info/10 text-info border-info/30' },
  ],
};

/**
 * One trip: its lifecycle, and who was on the bus.
 *
 * <p>The roster works like the class register: an unmarked child shows as
 * unmarked, never defaulted to boarded, and each tap posts immediately — a
 * single mark is simply a list of one, and the server answers with the fresh
 * manifest, which replaces the cache rather than being recomputed here.
 *
 * <p>The lifecycle buttons show what the current status plausibly allows, but
 * every rule lives on the server: a benched vehicle, a double-booked driver or
 * a stale open trip comes back as a refusal with the reason in the server's
 * words.
 */
export function TripDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const manages = can(PERMISSIONS.TRANSPORT_TRIP_MANAGE);
  const viewsRoster = can(PERMISSIONS.TRANSPORT_ATTENDANCE_VIEW);
  const records = can(PERMISSIONS.TRANSPORT_ATTENDANCE_RECORD);
  const viewsVehicles = can(PERMISSIONS.VEHICLE_VIEW);
  const viewsDrivers = can(PERMISSIONS.TRANSPORT_DRIVER_VIEW);

  const [delayOpen, setDelayOpen] = useState(false);
  const [delayForm, setDelayForm] = useState({ minutes: '', reason: '' });
  const [subOpen, setSubOpen] = useState(false);
  const [subForm, setSubForm] = useState({ vehicleId: '', driverId: '', attendantId: '' });

  const trip = useQuery({
    queryKey: queryKeys.trips.detail(id),
    queryFn: () => tripsApi.get(id),
  });

  const roster = useQuery({
    queryKey: queryKeys.trips.roster(id),
    queryFn: () => tripsApi.roster(id),
    enabled: viewsRoster,
  });

  /* Stand-in candidates, fetched only while the substitution dialog is open.
     A driver's eligibility is quoted from the server's own field — and the
     final say on an ineligible choice is the server's refusal, not ours. */
  const vehicles = useQuery({
    queryKey: queryKeys.vehicles.list({ picker: true, status: 'ACTIVE' }),
    queryFn: () => vehiclesApi.list({ page: 0, size: 200, status: 'ACTIVE' }),
    enabled: subOpen && viewsVehicles,
  });

  const drivers = useQuery({
    queryKey: queryKeys.transportDrivers.list({ picker: true, status: 'ACTIVE' }),
    queryFn: () => transportDriversApi.list({ page: 0, size: 200, status: 'ACTIVE' }),
    enabled: subOpen && viewsDrivers,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.trips.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
  };

  const start = useMutation({
    mutationFn: () => tripsApi.start(id),
    onSuccess: (updated) => {
      toast.success(`${updated.tripNumber} is ${humanise(updated.status)}`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const depart = useMutation({
    mutationFn: () => tripsApi.depart(id),
    onSuccess: (updated) => {
      toast.success(`${updated.tripNumber} is under way`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const complete = useMutation({
    mutationFn: () => tripsApi.complete(id),
    onSuccess: (updated) => {
      toast.success(`${updated.tripNumber} completed`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const delay = useMutation({
    mutationFn: () =>
      tripsApi.delay(
        id,
        Number(delayForm.minutes),
        delayForm.reason.trim() === '' ? undefined : delayForm.reason.trim(),
      ),
    onSuccess: (updated) => {
      toast.success(`${updated.tripNumber} marked ${delayText(updated.delayMinutes)}`);
      setDelayOpen(false);
      setDelayForm({ minutes: '', reason: '' });
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => tripsApi.cancel(id, reason),
    onSuccess: (updated) => {
      toast.info(`${updated.tripNumber} cancelled`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const substitute = useMutation({
    mutationFn: () =>
      tripsApi.substitute(id, {
        ...(subForm.vehicleId !== '' ? { vehicleId: subForm.vehicleId } : {}),
        ...(subForm.driverId !== '' ? { driverId: subForm.driverId } : {}),
        ...(subForm.attendantId !== '' ? { attendantId: subForm.attendantId } : {}),
      }),
    onSuccess: (updated) => {
      toast.success(`${updated.tripNumber} updated — the route's assignments are untouched`);
      setSubOpen(false);
      setSubForm({ vehicleId: '', driverId: '', attendantId: '' });
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const mark = useMutation({
    mutationFn: (marks: TripAttendanceMark[]) => tripsApi.mark(id, { marks }),
    onSuccess: (updated) => {
      // The server answers with the fresh manifest — it replaces the cache
      // rather than the counts being recomputed here.
      queryClient.setQueryData(queryKeys.trips.roster(id), updated);
      void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
    },
    onError: (error) => handleApiError(error),
  });

  if (trip.isError) {
    return <ErrorState error={trip.error} onRetry={() => void trip.refetch()} />;
  }
  if (trip.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = trip.data;
  const live = data.status !== 'COMPLETED' && data.status !== 'CANCELLED';
  const markOptions = MARKS[data.direction];
  const restLabel = data.direction === 'PICKUP' ? 'Mark the rest boarded' : 'Mark the rest dropped off';
  const restStatus: TransportAttendanceStatus =
    data.direction === 'PICKUP' ? 'BOARDED' : 'DROPPED_OFF';
  const unmarkedRiders = (roster.data?.riders ?? []).filter((rider) => rider.status === undefined);

  return (
    <div>
      <Link
        to={ROUTES.trips}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Trips
      </Link>

      <PageHeader
        title={data.tripNumber}
        description={`${data.routeName} · ${humanise(data.direction)} · ${formatDate(data.tripDate)}`}
        icon={<Navigation className="h-5 w-5" aria-hidden="true" />}
        action={
          <span className="flex flex-wrap items-center gap-2">
            <TripStatusBadge status={data.status} />
            {manages && data.status === 'SCHEDULED' && (
              <Button onClick={() => start.mutate()} disabled={start.isPending}>
                Open boarding
              </Button>
            )}
            {manages &&
              (data.status === 'SCHEDULED' ||
                data.status === 'BOARDING' ||
                data.status === 'DELAYED') && (
                <Button onClick={() => depart.mutate()} disabled={depart.isPending}>
                  Depart
                </Button>
              )}
            {manages &&
              (data.status === 'BOARDING' ||
                data.status === 'IN_PROGRESS' ||
                data.status === 'DELAYED') && (
                <Button
                  variant="secondary"
                  onClick={() => complete.mutate()}
                  disabled={complete.isPending}
                >
                  Complete
                </Button>
              )}
            {manages && live && (
              <>
                <Button variant="ghost" onClick={() => setDelayOpen(true)}>
                  <Timer className="h-4 w-4" aria-hidden="true" />
                  Delay
                </Button>
                <Button variant="ghost" onClick={() => setSubOpen(true)}>
                  <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
                  Substitute
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    const reason = window.prompt('Why is the trip being cancelled?');
                    if (reason !== null && reason.trim() !== '') {
                      cancel.mutate(reason.trim());
                    }
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </span>
        }
      />

      {viewsRoster && roster.data !== undefined && (
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Expected" value={roster.data.expected} />
          <Stat
            label="Boarded"
            value={roster.data.boarded}
            tone={roster.data.boarded > 0 ? 'success' : 'neutral'}
          />
          <Stat
            label="Unmarked"
            value={roster.data.unmarked}
            tone={roster.data.unmarked > 0 ? 'warning' : 'neutral'}
            hint={roster.data.unmarked > 0 ? 'Nobody has marked these rows' : 'All accounted for'}
          />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {viewsRoster && (
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-4 sm:px-5">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Who rides
              </h2>
              {records && data.status !== 'CANCELLED' && unmarkedRiders.length > 0 && (
                // The common case is "everyone got on, except…" — fill the
                // sheet in one action, then correct the exceptions.
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={mark.isPending}
                  onClick={() =>
                    mark.mutate(
                      unmarkedRiders.map((rider) => ({
                        enrollmentId: rider.enrollmentId,
                        status: restStatus,
                      })),
                    )
                  }
                >
                  {restLabel}
                </Button>
              )}
            </div>

            {roster.isError ? (
              <ErrorState error={roster.error} onRetry={() => void roster.refetch()} />
            ) : roster.isPending ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : roster.data.riders.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted">
                Nobody is expected on this trip.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {roster.data.riders.map((rider) => (
                  <RiderRow
                    key={rider.enrollmentId}
                    rider={rider}
                    options={markOptions}
                    readOnly={!records || data.status === 'CANCELLED'}
                    busy={mark.isPending}
                    onMark={(status) =>
                      mark.mutate([{ enrollmentId: rider.enrollmentId, status }])
                    }
                  />
                ))}
              </ul>
            )}
          </Card>
        )}

        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Trip</h2>
          <dl className="mt-2">
            <Field label="Route">{data.routeName}</Field>
            <Field label="Vehicle">{orDash(data.vehicleName)}</Field>
            <Field label="Driver">{orDash(data.driverName)}</Field>
            <Field label="Attendant">{orDash(data.attendantName)}</Field>
            <Field label="Planned">
              {formatTime(data.plannedStartTime)} – {formatTime(data.plannedEndTime)}
            </Field>
            <Field label="Started">
              {data.actualStartAt === undefined ? '—' : formatDateTime(data.actualStartAt)}
            </Field>
            <Field label="Ended">
              {data.actualEndAt === undefined ? '—' : formatDateTime(data.actualEndAt)}
            </Field>
            {data.delayMinutes !== undefined && data.delayMinutes > 0 && (
              <Field label="Delay">
                {delayText(data.delayMinutes)}
                {data.delayReason !== undefined && ` — ${data.delayReason}`}
              </Field>
            )}
            {data.cancellationReason !== undefined && (
              <Field label="Cancelled because">{data.cancellationReason}</Field>
            )}
            {data.notes !== undefined && <Field label="Notes">{data.notes}</Field>}
          </dl>
        </Card>
      </div>

      <Modal
        open={delayOpen}
        title="Record a delay"
        description="When delay notices are switched on, every rider's guardian gets one."
        onClose={() => setDelayOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDelayOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={delay.isPending}
              disabled={delayForm.minutes === ''}
              onClick={() => delay.mutate()}
            >
              Record the delay
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField
            label="Minutes late"
            type="number"
            min={1}
            required
            value={delayForm.minutes}
            onChange={(event) => setDelayForm((form) => ({ ...form, minutes: event.target.value }))}
          />
          <TextField
            label="Reason"
            value={delayForm.reason}
            hint="Goes into the guardian notice as written."
            onChange={(event) => setDelayForm((form) => ({ ...form, reason: event.target.value }))}
          />
        </div>
      </Modal>

      <Modal
        open={subOpen}
        title="Substitute for this trip"
        description="The Tuesday stand-in: lives on this trip only, the route's assignment history untouched."
        onClose={() => setSubOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setSubOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={substitute.isPending}
              disabled={
                subForm.vehicleId === '' && subForm.driverId === '' && subForm.attendantId === ''
              }
              onClick={() => substitute.mutate()}
            >
              Swap them in
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {viewsVehicles && (
            <SelectField
              label="Vehicle"
              value={subForm.vehicleId}
              hint={`Currently ${orDash(data.vehicleName)}.`}
              onChange={(event) =>
                setSubForm((form) => ({ ...form, vehicleId: event.target.value }))
              }
            >
              <option value="">Keep the current one</option>
              {(vehicles.data?.data ?? []).map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.displayName} · {vehicle.seatingCapacity} seats
                </option>
              ))}
            </SelectField>
          )}

          {viewsDrivers && (
            <>
              <SelectField
                label="Driver"
                value={subForm.driverId}
                hint={`Currently ${orDash(data.driverName)}. An ineligible driver is refused with the exact blockers.`}
                onChange={(event) =>
                  setSubForm((form) => ({ ...form, driverId: event.target.value }))
                }
              >
                <option value="">Keep the current one</option>
                {(drivers.data?.data ?? [])
                  .filter((driver) => driver.driverRole === 'DRIVER')
                  .map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.employeeName}
                      {driver.tripEligible ? '' : ' — not eligible today'}
                    </option>
                  ))}
              </SelectField>

              <SelectField
                label="Attendant"
                value={subForm.attendantId}
                hint={`Currently ${orDash(data.attendantName)}.`}
                onChange={(event) =>
                  setSubForm((form) => ({ ...form, attendantId: event.target.value }))
                }
              >
                <option value="">Keep the current one</option>
                {(drivers.data?.data ?? [])
                  .filter((driver) => driver.driverRole === 'ATTENDANT')
                  .map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.employeeName}
                      {driver.tripEligible ? '' : ' — not eligible today'}
                    </option>
                  ))}
              </SelectField>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

/**
 * One rider, tap-to-mark.
 *
 * Sized for a thumb on a phone held one-handed at a bus door — the roster is
 * marked standing on the kerb, not at a desk. An unmarked row says so out
 * loud; it is never defaulted to boarded.
 */
function RiderRow({
  rider,
  options,
  readOnly,
  busy,
  onMark,
}: {
  rider: TripRider;
  options: { value: TransportAttendanceStatus; label: string; tone: string }[];
  readOnly: boolean;
  busy: boolean;
  onMark: (status: TransportAttendanceStatus) => void;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
          {rider.studentName}
          {rider.guest && (
            // Riding a route their enrollment does not name — flagged, always.
            <Badge tone="info">guest</Badge>
          )}
        </p>
        <p className="text-xs text-subtle">
          {orDash(rider.studentNumber)} · {rider.stopName ?? 'no stop recorded'}
        </p>
        {(rider.guardianName !== undefined || rider.guardianPhone !== undefined) && (
          <p className="text-xs text-muted">
            {orDash(rider.guardianName)}
            {rider.guardianPhone !== undefined && ` · ${rider.guardianPhone}`}
          </p>
        )}
        {rider.specialInstructions !== undefined && (
          <p className="text-xs text-[color:var(--sms-warning)]">{rider.specialInstructions}</p>
        )}
      </div>

      {readOnly ? (
        <AttendanceStatusBadge status={rider.status} />
      ) : (
        <div
          className="flex flex-wrap gap-1"
          role="group"
          aria-label={`Attendance for ${rider.studentName}`}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={rider.status === option.value}
              disabled={busy}
              onClick={() => onMark(option.value)}
              className={`min-h-10 rounded-md border px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${
                rider.status === option.value
                  ? option.tone
                  : 'border-line text-subtle hover:border-line-strong hover:text-muted'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
